import type { GeoUnitTypeKey } from '../../types/geoAnalytics'

/**
 * Virginia (state FIPS 51) county + independent-city names, keyed by the 3-digit
 * county-level FIPS code. Source: U.S. Census Bureau reference file
 * `www2.census.gov/geo/docs/reference/codes/files/st51_va_cou.txt`.
 *
 * The seed is VA-only today, so this table is intentionally VA-scoped — a GEOID
 * whose state prefix is not "51" resolves to no county and the label drops the
 * county segment (see formatGeoUnitLabel). Independent cities carry a "(city)"
 * suffix so the four county/city name collisions (Fairfax, Franklin, Richmond,
 * Roanoke) stay unambiguous.
 */
export const VA_COUNTY_FIPS: Record<string, string> = {
  // Counties
  '001': 'Accomack',
  '003': 'Albemarle',
  '005': 'Alleghany',
  '007': 'Amelia',
  '009': 'Amherst',
  '011': 'Appomattox',
  '013': 'Arlington',
  '015': 'Augusta',
  '017': 'Bath',
  '019': 'Bedford',
  '021': 'Bland',
  '023': 'Botetourt',
  '025': 'Brunswick',
  '027': 'Buchanan',
  '029': 'Buckingham',
  '031': 'Campbell',
  '033': 'Caroline',
  '035': 'Carroll',
  '036': 'Charles City',
  '037': 'Charlotte',
  '041': 'Chesterfield',
  '043': 'Clarke',
  '045': 'Craig',
  '047': 'Culpeper',
  '049': 'Cumberland',
  '051': 'Dickenson',
  '053': 'Dinwiddie',
  '057': 'Essex',
  '059': 'Fairfax',
  '061': 'Fauquier',
  '063': 'Floyd',
  '065': 'Fluvanna',
  '067': 'Franklin',
  '069': 'Frederick',
  '071': 'Giles',
  '073': 'Gloucester',
  '075': 'Goochland',
  '077': 'Grayson',
  '079': 'Greene',
  '081': 'Greensville',
  '083': 'Halifax',
  '085': 'Hanover',
  '087': 'Henrico',
  '089': 'Henry',
  '091': 'Highland',
  '093': 'Isle of Wight',
  '095': 'James City',
  '097': 'King and Queen',
  '099': 'King George',
  '101': 'King William',
  '103': 'Lancaster',
  '105': 'Lee',
  '107': 'Loudoun',
  '109': 'Louisa',
  '111': 'Lunenburg',
  '113': 'Madison',
  '115': 'Mathews',
  '117': 'Mecklenburg',
  '119': 'Middlesex',
  '121': 'Montgomery',
  '125': 'Nelson',
  '127': 'New Kent',
  '131': 'Northampton',
  '133': 'Northumberland',
  '135': 'Nottoway',
  '137': 'Orange',
  '139': 'Page',
  '141': 'Patrick',
  '143': 'Pittsylvania',
  '145': 'Powhatan',
  '147': 'Prince Edward',
  '149': 'Prince George',
  '153': 'Prince William',
  '155': 'Pulaski',
  '157': 'Rappahannock',
  '159': 'Richmond',
  '161': 'Roanoke',
  '163': 'Rockbridge',
  '165': 'Rockingham',
  '167': 'Russell',
  '169': 'Scott',
  '171': 'Shenandoah',
  '173': 'Smyth',
  '175': 'Southampton',
  '177': 'Spotsylvania',
  '179': 'Stafford',
  '181': 'Surry',
  '183': 'Sussex',
  '185': 'Tazewell',
  '187': 'Warren',
  '191': 'Washington',
  '193': 'Westmoreland',
  '195': 'Wise',
  '197': 'Wythe',
  '199': 'York',
  // Independent cities
  '510': 'Alexandria (city)',
  '515': 'Bedford (city)',
  '520': 'Bristol (city)',
  '530': 'Buena Vista (city)',
  '540': 'Charlottesville (city)',
  '550': 'Chesapeake (city)',
  '570': 'Colonial Heights (city)',
  '580': 'Covington (city)',
  '590': 'Danville (city)',
  '595': 'Emporia (city)',
  '600': 'Fairfax (city)',
  '610': 'Falls Church (city)',
  '620': 'Franklin (city)',
  '630': 'Fredericksburg (city)',
  '640': 'Galax (city)',
  '650': 'Hampton (city)',
  '660': 'Harrisonburg (city)',
  '670': 'Hopewell (city)',
  '678': 'Lexington (city)',
  '680': 'Lynchburg (city)',
  '683': 'Manassas (city)',
  '685': 'Manassas Park (city)',
  '690': 'Martinsville (city)',
  '700': 'Newport News (city)',
  '710': 'Norfolk (city)',
  '720': 'Norton (city)',
  '730': 'Petersburg (city)',
  '735': 'Poquoson (city)',
  '740': 'Portsmouth (city)',
  '750': 'Radford (city)',
  '760': 'Richmond (city)',
  '770': 'Roanoke (city)',
  '775': 'Salem (city)',
  '790': 'Staunton (city)',
  '800': 'Suffolk (city)',
  '810': 'Virginia Beach (city)',
  '820': 'Waynesboro (city)',
  '830': 'Williamsburg (city)',
  '840': 'Winchester (city)',
}

/**
 * Format a 6-digit census tract code into the standard dotted display form:
 * the last two digits are the decimal suffix, leading zeros in the integer part
 * are stripped, and a ".00" suffix collapses. e.g. "930202" → "9302.02",
 * "000100" → "1", "001002" → "10.02". Non-6-digit input is returned unchanged.
 */
export function formatTractCode(tract6: string): string {
  if (!/^\d{6}$/.test(tract6)) return tract6
  const intPart = String(parseInt(tract6.slice(0, 4), 10))
  const frac = tract6.slice(4)
  return frac === '00' ? intPart : `${intPart}.${frac}`
}

/**
 * Resolve the VA county/city name from a tract or block-group GEOID
 * (state2 + county3 + …). Returns null when the state prefix is not VA (51) or
 * the county code is unknown, so callers can omit the county segment gracefully.
 */
export function countyNameFromGeoid(geoid: string): string | null {
  if (geoid.length < 5) return null
  if (geoid.slice(0, 2) !== '51') return null
  return VA_COUNTY_FIPS[geoid.slice(2, 5)] ?? null
}

/**
 * Compose a disambiguating display label for a geographic unit.
 *
 * Block group (GEOID SSCCCTTTTTTB, 12 digits) → "BG 1 · Tract 9302.02 · Fairfax"
 * Census tract (GEOID SSCCCTTTTTT, 11 digits) → "Tract 9302.02 · Fairfax"
 *
 * The TIGER GeoUnit.Name for these units is a meaningless "Block Group 1" /
 * "Census Tract 9302.02" that repeats statewide, so we rebuild the label from
 * the GEOID instead. Any other geography (districts, counties, precincts, …)
 * keeps its own name unchanged. If the code isn't the expected length (or the
 * county can't be resolved), we degrade to the best available label rather than
 * showing an empty string.
 */
export function formatGeoUnitLabel(
  unitType: GeoUnitTypeKey | undefined,
  code: string | undefined,
  name: string | undefined,
): string {
  const fallback = name ?? code ?? ''
  if (!code) return fallback

  if (unitType === 'censusBlockGroup' && /^\d{12}$/.test(code)) {
    const tract = formatTractCode(code.slice(5, 11))
    const bg = code.slice(11)
    const county = countyNameFromGeoid(code)
    return `BG ${bg} · Tract ${tract}${county ? ` · ${county}` : ''}`
  }

  if (unitType === 'censusTract' && /^\d{11}$/.test(code)) {
    const tract = formatTractCode(code.slice(5, 11))
    const county = countyNameFromGeoid(code)
    return `Tract ${tract}${county ? ` · ${county}` : ''}`
  }

  return fallback
}
